import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import '../../../../public/style/login.scss';
import '../../../../public/style/slick.scss';
import '../../../../public/style/slick-theme.scss';

/*img */
import Logo from '../../../../public/images/logo-gauss.png';
import LogoDesert from '../../../../public/images/logo-desertpoint.png';
import { getRedirectUrlSSOApi } from '../../actions/login';
import LoadingModal from '../common/loadingModal';
import { VersionPackage } from '../common/versionPackage';

/**
 * definition of the steps for the login flow
 */
const stepLogin = {
  username: { index: 0, name: 'username' },
  password: { index: 1, name: 'password' }
}

/**
 * Slider component settings
 */
const settingsSlider = {
  dots: false,
  infinite: false,
  speed: 600,
  slidesToShow: 1,
  slidesToScroll: 1,
  swipe: false,
  adaptiveHeight: true,
  arrows: false,
};

const LoginSSO = (props) => {
  const [userLogin, setUserLogin] = useState({username: '', password: ''});
  const [currentStep, setCurrentStep] = useState(stepLogin.username.name);
  const [errorMessage, setErrorMessage] = useState('');
  const [textLoginButton, setTextLoginButton] = useState(props.language.login.main.next);
  const [disabledLoginButton, setDisabledLoginButton] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const sliderRef = useRef(null);
  let timeoutIsRendered;

  /**
   * initial method that is executed the first time the component is loaded, in which the initial values ​​of the states are set
   */
  useEffect(() => {
    setViewCurrentStep(currentStep);
    timeoutIsRendered = setTimeout(() => {
      setIsRendered(true);
    }, 100);

    return () => {
      clearTimeout(timeoutIsRendered);
    }
  }, []);

  /**
   * this method that runs every time the "prop.loading" property changes its value, where the state of the error message is changed
   */
  useEffect(() => {
    if (props.message) {
      setErrorMessage(props.language.login.main.error);
    };
  }, [props.loading]);

  /**
   * sets the values ​​of the states according to the step to be displayed
   * @param {string} step step to be shown
   */
  const setViewCurrentStep = (step) => {
    setErrorMessage('');
    setCurrentStep(step);
    setDisabledLoginButton(false);

    switch (step) {
      case stepLogin.username.name:
        setTextLoginButton(props.language.login.main.next);
        setUserLogin({ ...userLogin, password: '' });
        break;
      case stepLogin.password.name:
        setTextLoginButton(props.language.login.main.loginButton);
        break;
      default:
        break;
    }
  }

  /**
   * validates if the user has an SSO url configured to log in or does the traditional way
   */
  const validateLogin = async () => {
    switch (currentStep) {
      case stepLogin.username.name:
        if (userLogin.username === '') {
          setErrorMessage(props.language.login.main.emailRequired);
          return;
        }

        // validates if you have an external url to login with SSO
        setDisabledLoginButton(true);
        setTextLoginButton(props.language.login.main.validating);
        setErrorMessage('');
        await validateRedirectToSSO();

        break;
      case stepLogin.password.name:
        if (userLogin.password === '') {
          setErrorMessage(props.language.login.main.passwordRequired);
          return;
        }

        props.handleLogin(userLogin);
        break;
    }
  }

  /**
   * query the API if you have an SSO url configured
   */
  const validateRedirectToSSO = async () => {
    const arrUsername = userLogin.username.split('@');
    const domain = (arrUsername.length > 0) ? arrUsername[1] : obj.username;

    const resp = await getRedirectUrlSSOApi({ domain });
    const { data } = resp;
    const { error, response } = data;

    // if there is an error, it shows a message and does not go to the next step.
    if (error) {
      setErrorMessage(`${props.language.login.main.errorGetUrlSSO} (ERR: ${error.message || error})`);
      setTextLoginButton(props.language.login.main.next);
      setDisabledLoginButton(false);
    } else {
      const { externalLoginUrl, hasExternalLogin } = response;
      
      if (hasExternalLogin) {
        window.location.href = externalLoginUrl;
        return;
      } else {
        // update component states
        setViewCurrentStep(stepLogin.password.name);
  
        // move to the next step
        slickGoTo(stepLogin.password.index);
      }
    }
  }

  /**
   * assigns the value of the input in the property of the userLogin object
   * @param {object} e event sent from the control that called this method
   * @param {string} type type of the input where the value should be assigned
   */
  const handleChangeInput = (e, type) => {
    setUserLogin({ ...userLogin, [type]: e.target.value });
  }

  /**
   * handles the Back button action
   */
  const handleBack = () => {
    setViewCurrentStep(stepLogin.username.name);
    slickGoTo(stepLogin.username.index);
  }

  /**
   * move the slider to the requested index
   * @param {number} sliderIndex index of the slide to be displayed
   */
  const slickGoTo = (sliderIndex) => {
    sliderRef.current.slickGoTo(sliderIndex);
  }

  /**
   * This method handles keyboard events
   */
  const handleKeyboardEvent = async (event) => {
    const tabKeyCode = 9;
    const enterKeyCode = 13;

    // check if the pressed key is the Tab key (key code 9)
    if (event.keyCode === tabKeyCode) {
      event.preventDefault(); // Prevent the default behavior (changing focus)
    }

    // check if the pressed key is the Enter key (key code 13)
    if (event.keyCode === enterKeyCode) {
      await validateLogin();
    }
  }

  // when the component is just loaded, it shows a message "Loading..."
  if (!isRendered) {
    return(<div>{props.language.login.main.loading} ...</div>);
  }

  // when finished rendering returns the complete component
  return (
    <div className="container_login" onKeyDown={handleKeyboardEvent}>
      <div className="container_login_box">
        <div className="container_login_img">
          <img src={Logo} alt="Gauss control" />
        </div>
        <div className="container_login_form">
            <h1>{props.language.login.main.login}</h1>

            <Slider {...settingsSlider} ref={sliderRef}>
              <div className="box-form icon icon_user">
                <input
                  type="text"
                  className="login"
                  id="user"
                  placeholder={props.language.login.main.email}
                  autoComplete="username"
                  value={userLogin.username}
                  onChange={(e) => handleChangeInput(e, stepLogin.username.name)}
                />
              </div>
              {
                (currentStep === stepLogin.password.name) && (
                  <div>
                    <p className="login__label-username">{props.language.login.main.email} <span>{userLogin.username}</span></p>
                    <div className="box-form icon icon_pass top">
                      <input
                        type="password"
                        className="login"
                        id="password"
                        placeholder={props.language.login.main.password}
                        autoComplete="new-password"
                        value={userLogin.password}
                        onChange={(e) => handleChangeInput(e, stepLogin.password.name)}
                      />
                    </div>
                  </div>
                )
              }
            </Slider>

            <div className="box-form top">
              {
                errorMessage && (
                  <p className="message"><i className="fas fa-times-circle mr-1" />{errorMessage}</p>
                )
              }
            </div>
            <div className="box-form top topx2 center">
              {
                (currentStep === stepLogin.password.name) && (
                  <input type="button" className="back-login animate__animated animate__fadeIn" value={props.language.login.main.back} onClick={handleBack} />
                )
              }
              <input type="button" className="login" value={textLoginButton} onClick={validateLogin} disabled={disabledLoginButton} />
            </div>
          {props.loading && (
            <LoadingModal language={props.language}/>
          )}
        </div>
        <div className="container_login_img__bottom">
          <p>{props.language.login.main.developedBy}</p>
          <img src={LogoDesert} alt="Desert point" />
        </div>  
      </div>
      <div className="container_login_version">
        {
          props.showVersion && (
            <VersionPackage />
          )
        }
      </div>
    </div>
  )
};

export default LoginSSO;



// WEBPACK FOOTER //
// ./source/client/components/loginSSO/index.jsx